import {
  AssignableTypeEnum,
  RequestBasePostClubTask,
  RequestBasePostTaskAutoAssignmentPreview,
  RequestBasePostUserInvitationLink,
  RequestBasePostUserTermination,
  RequestBaseUpdateClubWelcomeText,
  RequestBaseUpdateTask,
  RequestBaseUpdateUserSettings,
  RequestBasketballPostMessage,
  RequestBasketballPostPracticeHelpouts,
  RequestBasketballPostTeamGameHelpouts,
  RequestBasketballPostTeamJudgesUsersAutoAssignment,
  RequestBasketballPostTeamTask,
  RequestBasketballUpdateJudgeTeam,
  RequestBasketballUpdateJudgeUser,
  RequestBasketballUpdateOwnReferee,
  RequestBasketballUpdatePracticeNominees,
  RequestBasketballUpdateResponsibleUser,
  RequestBasketballUpdateSeasonPlanerTasks,
  RequestBasketballUpdateTeamGameCars,
  RequestBasketballUpdateTeamGameMeetAt,
  RequestBasketballUpdateTeamGameNominees,
  RequestStripePostCheckout,
  UserInvitationLink,
  UserTermination,
} from '@hefe/models';
import { fetchBaseQuery } from '@reduxjs/toolkit/query';
import axios from 'axios';
import { environment } from '../../environments/environment'; // Set this to .prod before building app for production!!
import {
  BasketballEventAddCommentRequest,
  BasketballUserEventsRequest,
} from '../models/requests/basketball-event';
import { BasketballGameReplyRequest } from '../models/requests/basketball-game';
import { DbbBasketballLeagueCreateRequest } from '../models/requests/basketball-league';
import { BasketballPlayerEditRequest } from '../models/requests/basketball-player';
import {
  BasketballPracticeReplyRequest,
  BasketballPracticesLinkRequest,
  BasketballPracticesSaveRequest,
  BasketballPracticesUpdateRequest,
} from '../models/requests/basketball-practice';
import {
  BasketballSwitchRequestCreateRequest,
  BasketballSwitchRequestTakeOverJudgeGameRequest,
  BasketballSwitchRequestTakeOverRequest,
} from '../models/requests/basketball-switch-request';
import {
  BasketballTeamAddPlayersRequest,
  BasketballTeamRemovePlayersRequest,
  BasketballTeamTransferPlayersRequest,
  BasketballTeamsSyncPracticesUntilRequest,
} from '../models/requests/basketball-team';

enum ApiVersion {
  V1 = '/',
  V2 = '/v2/',
}
export default class API {
  static baseurl = environment.backendUrl; //process.env['NX_BACKEND_HOST']; // 'http://192.168.2.209:8000/api'; //

  static baseQuery = fetchBaseQuery({
    baseUrl: environment.backendUrl,
    prepareHeaders: (headers, { getState }) => {
      // Add any headers that you want to include in all requests
      const token = (getState as any)().authentication.token;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  });

  static appointments() {
    const url = 'appointments';
    const cluburl = 'club/appointments';

    return {
      ...API.getCRUDOperations(cluburl, url, ApiVersion.V1),
      clubCalendarAppointments: () => API.get('/calendar', ApiVersion.V1),
      clubCalendarUsers: () => API.get('/calendar/users', ApiVersion.V1),
    };
  }

  static cars() {
    const cluburl = `club/cars`;
    const url = `cars`;

    return API.getCRUDOperations(cluburl, url, ApiVersion.V1);
  }

  static carReservations() {
    const cluburl = `club/cars/reservations`;
    const url = `carreservations`;

    return API.getCRUDOperations(cluburl, url, ApiVersion.V1);
  }

  static club() {
    const cluburl = `club`;

    return {
      clubdata: () => API.get(cluburl, ApiVersion.V1),
      modules: () => API.get(cluburl + '/modules', ApiVersion.V1),
      update: (data: any) => API.updateNoID(cluburl, ApiVersion.V1, data),
      updateSettings: (data: any) =>
        API.updateNoID(cluburl + '/settings', ApiVersion.V1, data),
      updateWelcomeText: (data: RequestBaseUpdateClubWelcomeText) =>
        API.updateNoID(cluburl + '/welcometext', ApiVersion.V1, data),
      uploadLogo: (logo: any) =>
        API.uploadFile(cluburl + '/logo', ApiVersion.V1, logo),
      deleteLogo: () => API.deleteNoID(cluburl + '/logo', ApiVersion.V1),
      getDataProcessingAgreement: () =>
        API.get(cluburl + '/data-processing-agreement', ApiVersion.V1),
      agreeToDataProcessingAgreement: () =>
        API.create(
          cluburl + '/data-processing-agreement/accept',
          ApiVersion.V1,
          []
        ),
    };
  }

  static clubCustomUserAttributes() {
    const cluburl = `club/userattributes`;
    const url = `club/userattributes`;

    return API.getCRUDOperations(cluburl, url, ApiVersion.V1);
  }

  static contacts() {
    const resource = 'contacts';
    const url = `${resource}`;
    const cluburl = `club/${resource}`;

    return {
      ...API.getCRUDOperations(cluburl, url, ApiVersion.V1),
      allSelect: () => API.get(`${cluburl}/select`, ApiVersion.V1),
    };
  }

  static feeCategories() {
    const cluburl = `club/feecategories`;
    const url = `feecategories`;

    return API.getCRUDOperations(cluburl, url, ApiVersion.V1);
  }

  static gyms() {
    const cluburl = `club/gyms`;
    const url = `gyms`;

    return {
      ...API.getCRUDOperations(cluburl, url, ApiVersion.V1),
      getSpectators: (id: number, data: any) =>
        API.create(url + '/' + id + '/spectators', ApiVersion.V1, data),
      getSpectatorStatus: () =>
        API.get(cluburl + '/spectatorstatus', ApiVersion.V1),
    };
  }

  static keys() {
    const url = `keys`;

    return {
      ...API.getCRUDOperations(url, url, ApiVersion.V1),
      getGymKeys: (gym_id: number) =>
        API.get(url + '?gym=' + gym_id, ApiVersion.V1),
      holders: (id: number) =>
        API.getCRUDOperations(
          `${url}/${id}/holders`,
          `${url}/${id}/holders`,
          ApiVersion.V1
        ),
    };
  }

  static inventories() {
    const cluburl = `club/inventories`;
    const url = `inventories`;

    return API.getCRUDOperations(cluburl, url, ApiVersion.V1);
  }

  static members() {
    const cluburl = `club/users`;
    const url = `users`;

    return {
      ...API.getCRUDOperations(cluburl, url, ApiVersion.V2),
      assignModuleToUsers: (data: any) =>
        API.create(cluburl + '/assignModule', ApiVersion.V1, data),
      dissociateModuleFromUsers: (data: any) =>
        API.create(cluburl + '/dissociateModule', ApiVersion.V1, data),
      basketballMembers: () => API.get(cluburl + '/1', ApiVersion.V1),
      updateUserRights: (data: any) =>
        API.updateNoID(url + '/' + data.id + '/rights', ApiVersion.V2, data),
      updateChild: (data: any) =>
        API.updateNoID(
          url + '/' + data.parent_id + '/child',
          ApiVersion.V1,
          data
        ),
      updateChildren: (data: any) =>
        API.updateNoID(url + '/' + data.id + '/children', ApiVersion.V2, data),
      inviteMember: (data: string[]) =>
        API.create(url + '/invite', ApiVersion.V1, { emails: data }),
      getInvitationLinks: () => API.get(url + '/invite/links', ApiVersion.V1),
      generateInvitationLink: (request: RequestBasePostUserInvitationLink) =>
        API.create(url + '/invite/link', ApiVersion.V1, request),
      deleteInvitationLink: (link: UserInvitationLink) =>
        API.delete(url + '/invite/links', ApiVersion.V1, link),
      sendWelcomeMails: (data: number[]) =>
        API.create(cluburl + '/welcomeReminder', ApiVersion.V1, {
          user_ids: data,
        }),
      activateMembers: (data: number[]) =>
        API.create(url + '/activate', ApiVersion.V2, { user_ids: data }),
      deleteMulti: (data: number[]) =>
        API.create(url + '/destroymulti', ApiVersion.V2, { user_ids: data }),
      createTermination: (data: RequestBasePostUserTermination) =>
        API.create(`${url}/${data.user_id}/termination`, ApiVersion.V2, data),
      deleteTermination: (data: UserTermination) =>
        API.delete(`${url}/${data.user_id}/termination`, ApiVersion.V2, data),
    };
  }

  static membersImport() {
    const url = `users/import`;
    return {
      uploadFile: (file: any) =>
        API.uploadFile(url + '/upload', ApiVersion.V1, file),
      getPreview: (data: any) =>
        API.create(url + '/preview', ApiVersion.V1, data),
      saveUsers: (data: any) => API.create(url + '/save', ApiVersion.V1, data),
    };
  }

  static messages() {
    const userurl = `user/messages`;
    const url = `messages`;

    return {
      create: (data: any) => API.create(url, ApiVersion.V1, data),
      update: (data: any) => API.update(url, ApiVersion.V1, data),
      getFirstMessages: () => API.get(userurl, ApiVersion.V1),
      getMoreMessages: (url: string) => API.get(url, ApiVersion.V1),
      getRelatedMessages: (id: number) =>
        API.get(url + '/' + id + '/related', ApiVersion.V1),
    };
  }

  static registration() {
    const url = `register`;

    return {
      validRegistrationToken: (token: string) =>
        API.get(url + '/token/' + token, ApiVersion.V1),
      register: (data: any) => API.create(url, ApiVersion.V1, data),
    };
  }

  static settings() {
    const resource = 'settings';
    const url = `${resource}`;

    return {
      notifications: () => API.get(url + '/notificationtypes', ApiVersion.V1),
      notificationTypesUpdates: () =>
        API.get(url + '/notifications/updates', ApiVersion.V1),
    };
  }

  static stripe() {
    const resource = 'stripe';
    const url = `${resource}`;

    return {
      getBillingPortal: () => API.get(url + '/billing-portal', ApiVersion.V1),
      createCheckoutSession: (data: RequestStripePostCheckout) =>
        API.create(url + '/checkout', ApiVersion.V1, data),
    };
  }

  static tasks() {
    const resource = 'tasks';

    return {
      getClubTasks: () => API.get(`club/tasks`, ApiVersion.V1),
      createClubTasks: (data: RequestBasePostClubTask) =>
        API.create(`club/tasks`, ApiVersion.V1, data),
      getBasketballTeamTasks: (teamId: number, withClubTasks: boolean) =>
        API.get(
          `basketball/teams/${teamId}/tasks${
            withClubTasks ? '?include-club-tasks=true' : ''
          }`,
          ApiVersion.V1
        ),
      createBasketballTeamTask: (
        teamId: number,
        data: RequestBasketballPostTeamTask
      ) => API.create(`basketball/teams/${teamId}/tasks`, ApiVersion.V1, data),
      getAutoAssignmentsAssignables: (
        taskId: number,
        type: AssignableTypeEnum,
        basketballTeamId?: number
      ) =>
        API.get(
          `tasks/${taskId}/auto-assignments/assignables?type=${type}${
            basketballTeamId ? '&basketball-team-id=' + basketballTeamId : ''
          }`,
          ApiVersion.V1
        ),
      getAutoAssignmentsPreview: (
        taskId: number,
        data: RequestBasePostTaskAutoAssignmentPreview
      ) =>
        API.create(
          `tasks/${taskId}/auto-assignments/preview`,
          ApiVersion.V1,
          data
        ),
      update: (taskId: number, data: RequestBaseUpdateTask) =>
        API.updateNoID(`tasks/${taskId}`, ApiVersion.V1, data),
      delete: (id: number) => API.deleteNoID(`tasks/${id}`, ApiVersion.V1),
    };
  }

  static user() {
    const url = `user`;
    const urllogin = `login`;
    const urlResetPassword = `resetPassword`;
    const urlNotifications = `exponent/devices`;

    return {
      logout: () => API.create(url + '/logout', ApiVersion.V1, {}),
      login: (data: any) => API.create(urllogin, ApiVersion.V2, data),
      markNotificationAsRead: (id: string) =>
        API.updateNoID(
          url + '/notification/' + id + '/markasread',
          ApiVersion.V2,
          {}
        ),
      markNotificationsAsRead: () =>
        API.updateNoID(url + '/notifications/markasread', ApiVersion.V2, {}),
      deleteNotifications: () =>
        API.deleteNoID(url + '/notifications', ApiVersion.V2),
      addNotificationToken: (token: string) =>
        API.create(urlNotifications + '/subscribe', ApiVersion.V1, {
          expo_token: token,
        }),
      deleteNotificationToken: (token: string) =>
        API.create(urlNotifications + '/unsubscribe', ApiVersion.V1, {
          expo_token: token,
        }),
      deleteAvatar: () => API.deleteNoID(url + '/avatar', ApiVersion.V1),
      notifications: (page: number) =>
        API.get(url + '/notifications?page=' + page, ApiVersion.V1),
      refresh: () => API.create(urllogin + '/refresh', ApiVersion.V1, null),
      userdata: () => API.get(url, ApiVersion.V2),
      tasks: () => API.get(url + '/tasks', ApiVersion.V1),
      childUsers: () => API.get(url + '/childusers', ApiVersion.V1),
      activeChild: (data: any) =>
        API.create(url + '/activechild', ApiVersion.V2, data),
      updateSettings: (data: RequestBaseUpdateUserSettings) =>
        API.updateNoID(url + '/settings', ApiVersion.V2, data),
      updateNotifications: (data: any) =>
        API.updateNoID(url + '/settings/notifications', ApiVersion.V1, data),
      uploadAvatar: (file: any) =>
        API.uploadFile(url + '/avatar', ApiVersion.V1, file),
      uploadAvatarAsBase64: (data: string) =>
        API.create(url + '/avatar', ApiVersion.V1, { base64: data }),
      requestPassword: (data: any) =>
        API.create(urlResetPassword + '/requestToken', ApiVersion.V1, data),
      resetPassword: (data: any) =>
        API.create(urlResetPassword + '/reset', ApiVersion.V1, data),
    };
  }

  /*
        BASKETBALL MODULE
     */

  static basketballCoachPresences() {
    const url = `user/basketball/coachpresences`;

    return {
      getAll: () => API.get(url, ApiVersion.V2),
      save: (data: any) => API.create(url, ApiVersion.V2, data),
      delete: (data: any) => API.delete(url, ApiVersion.V2, data),
    };
  }

  static basketballCoachSwitches() {
    const userurl = `user/basketball/coachswitches`;
    const cluburl = `club/basketball/coachswitches`;
    const url = `basketball/coachswitches`;

    return {
      ...API.getCRUDOperations(cluburl, url, ApiVersion.V1),
      getAllGames: () => API.get(cluburl + '/1', ApiVersion.V1),
      getAllPractices: () => API.get(cluburl + '/2', ApiVersion.V1),
      getPossibleUserGames: () => API.get(userurl + '/games', ApiVersion.V1),
      getPossibleUserPractices: () =>
        API.get(userurl + '/practices', ApiVersion.V1),
    };
  }

  static basketballGames() {
    const cluburl = `club/basketball/games`;
    const url = `basketball/games`;

    return {
      getAll: () => API.get(cluburl, ApiVersion.V2),
      getUpcoming: () => API.get(cluburl + '/upcoming', ApiVersion.V1),
      getArchive: () => API.get(cluburl + '/archive', ApiVersion.V1),
      getHomeGames: () => API.get(cluburl + '/home', ApiVersion.V1),
      getHomeGamesCalendar: () =>
        API.get(cluburl + '/home/calendar', ApiVersion.V1),
      getAwayGames: () => API.get(cluburl + '/away', ApiVersion.V1),
      getAwayGamesLite: () => API.get(cluburl + '/away/lite', ApiVersion.V1),
      getRefereeGames: () => API.get(cluburl + '/ownreferees', ApiVersion.V1),
      getAllOverview: () => API.get(cluburl + '/overview', ApiVersion.V1),
      getSingle: (id: number) => API.get(`${url}/${id}`, ApiVersion.V2),
      getFormData: (id: number) =>
        API.get(`${url}/${id}/form-data`, ApiVersion.V2),
      getSeasonPlaner: (kw?: number, year?: number) =>
        API.get(
          `${url}/seasonplaner?${kw ? 'weekOfYear=' + kw + '&' : ''}${
            year ? 'year=' + year : ''
          }`,
          ApiVersion.V2
        ),
      getPostpone: (id: number) =>
        API.get(`${url}/${id}/postpone`, ApiVersion.V1),
      getCarsInformation: (id: number, teamId: number) =>
        API.get(`${url}/${id}/cars?team-id=${teamId}`, ApiVersion.V2),
      carpool: (game_id: number) => {
        return {
          ...API.getCRUDOperations(
            `${url}/${game_id}/carpool`,
            `${url}/${game_id}/carpool`,
            ApiVersion.V1
          ),
          create: (data: any) =>
            API.create(`${url}/${game_id}/carpool`, ApiVersion.V2, data),
        };
      },
      saveGame: (data: any) => API.create(url, ApiVersion.V2, data),
      saveJerseys: (data: any) =>
        API.updateNoID(`${url}/jerseys`, ApiVersion.V1, data),
      saveReferees: (data: any) =>
        API.updateNoID(`${url}/referees`, ApiVersion.V1, data),
      delete: (data: any) => API.deleteNoID(`${url}/${data}`, ApiVersion.V2),
      updateCars: (gameid: number, data: RequestBasketballUpdateTeamGameCars) =>
        API.updateNoID(`${url}/${gameid}/cars`, ApiVersion.V2, data),
      updateHelpouts: (data: RequestBasketballPostTeamGameHelpouts) =>
        API.create(`helpouts/basketball/game`, ApiVersion.V1, data),
      updateJudgeTeam: (
        gameid: number,
        data: RequestBasketballUpdateJudgeTeam
      ) => API.updateNoID(`${url}/${gameid}/judges/team`, ApiVersion.V2, data),
      updateJudgeUser: (
        gameid: number,
        data: RequestBasketballUpdateJudgeUser
      ) => API.updateNoID(`${url}/${gameid}/judges/user`, ApiVersion.V2, data),
      updateOwnReferee: (
        gameid: number,
        data: RequestBasketballUpdateOwnReferee
      ) => API.updateNoID(`${url}/${gameid}/ownreferees`, ApiVersion.V2, data),
      updateResponsibleUser: (
        gameid: number,
        data: RequestBasketballUpdateResponsibleUser
      ) =>
        API.updateNoID(`${url}/${gameid}/responsibleuser`, ApiVersion.V2, data),
      updateMeetAt: (
        gameid: number,
        data: RequestBasketballUpdateTeamGameMeetAt
      ) => API.updateNoID(`${url}/${gameid}/meetat`, ApiVersion.V2, data),
      updateNominees: (
        gameid: number,
        data: RequestBasketballUpdateTeamGameNominees
      ) => API.updateNoID(`${url}/${gameid}/nominees`, ApiVersion.V2, data),
      updatePreferredDate: (gameid: number, data: any) =>
        API.updateNoID(`${url}/${gameid}/preferred`, ApiVersion.V1, data),
      updateReply: (data: BasketballGameReplyRequest) =>
        API.updateNoID(`${url}/reply`, ApiVersion.V2, data),
      updateReplyAsCoach: (gameid: number, data: any) =>
        API.updateNoID(`${url}/${gameid}/coachreply`, ApiVersion.V1, data),
      updateBasketballSeasonPlanerVisibleTasks: (
        data: RequestBasketballUpdateSeasonPlanerTasks
      ) => API.create(`${url}/seasonplaner/visible-tasks`, ApiVersion.V2, data),
      setHomeGame: (data: any) =>
        API.updateNoID(`${url}/ishome`, ApiVersion.V2, data),
      setOwnReferees: (data: any) =>
        API.updateNoID(`${url}/ownreferees`, ApiVersion.V2, data),
      setMeetingTime: (data: any) =>
        API.updateNoID(`${url}/meetingtime`, ApiVersion.V1, data),
      setTeamData: (data: any) =>
        API.updateNoID(`${url}/teamdata`, ApiVersion.V1, data),
      downloadReport: (id: number) =>
        API.download(url + '/' + id + '/report', ApiVersion.V1),
      downloadDBBReport: (id: number) =>
        API.download(url + '/' + id + '/dbbreport', ApiVersion.V1),
      downloadRefereeBill: (id: number) =>
        API.download(url + '/' + id + '/refereebill', ApiVersion.V1),
    };
  }

  static basketballGameComments(game_id: number) {
    const url = `basketball/games/${game_id}/comments`;
    const gameUrl = `basketball/games/${game_id}/`;
    return {
      getComments: (team_id: number, page: number) =>
        API.get(
          `${gameUrl}teams/${team_id}/comments?page=${page}`,
          ApiVersion.V2
        ),
      addComment: (data: BasketballEventAddCommentRequest) =>
        API.create(`${url}`, ApiVersion.V2, data),
    };
  }

  static basketballJerseySwitches() {
    const userurl = `user/basketball/jerseyswitches`;
    const url = `basketball/jerseyswitches`;

    return {
      ...API.getCRUDOperations(userurl, url, ApiVersion.V1),
      getAll: () => API.get(userurl, ApiVersion.V1),
      getPossibleUserGames: () => API.get(userurl + '/games', ApiVersion.V1),
    };
  }

  static basketballJudgeSwitches() {
    const userurl = `user/basketball/judgeswitches`;
    const url = `basketball/judgeswitches`;

    return {
      ...API.getCRUDOperations(userurl, url, ApiVersion.V1),
      getAll: () => API.get(userurl, ApiVersion.V1),
      getPossibleUserGames: () => API.get(userurl + '/games', ApiVersion.V1),
    };
  }

  static basketballLeagues() {
    const cluburl = `club/basketball/leagues`;
    const url = `basketball/leagues`;

    return {
      create: (data: DbbBasketballLeagueCreateRequest) =>
        API.create(url, ApiVersion.V1, data),
      update: (data: any) => API.update(url, ApiVersion.V1, data),
      delete: (data: any) => API.delete(url, ApiVersion.V1, data),
      getAll: () => API.get(cluburl, ApiVersion.V1),
      getPreview: (data: any) =>
        API.create(url + '/preview', ApiVersion.V1, data),
      getUpcomingData: (id: number) =>
        API.get(url + '/' + id + '/upcoming', ApiVersion.V1),
      getLeagueData: (id: number) => API.get(url + '/' + id, ApiVersion.V1),
      getTeamnames: (id: number) =>
        API.get(url + '/' + id + '/teams', ApiVersion.V1),
      scannDBBLeagues: (data: any) =>
        API.create(url + '/dbbScan', ApiVersion.V1, data),
      syncLeague: (id: number) => API.get(`${url}/${id}/sync`, ApiVersion.V1),
      getScoutingReport: (league_id: number, team_id: number) =>
        API.get(url + '/' + league_id + '/teamstats/' + team_id, ApiVersion.V1),
    };
  }

  static basketballUsers() {
    const cluburl = `club/basketball/users`;
    const url = `basketball/users`;

    return {
      getAll: () => API.get(cluburl, ApiVersion.V2),
      getAllSelect: () => API.get(cluburl + '/select', ApiVersion.V1),
      coaches: () => API.get(`club/basketball/coaches`, ApiVersion.V1),
      coachesConflicts: () =>
        API.get(`club/basketball/coaches/conflicts`, ApiVersion.V1),
      players: () => API.get(`club/basketball/players`, ApiVersion.V1),
      update: (data: any) => API.updateNoID(url, ApiVersion.V1, data),
    };
  }

  static basketballMessages() {
    const clubURL = `club/basketball/messages`;
    const userURL = `basketball/messages/user`;
    const teamURL = `basketball/messages/team`;
    const url = `basketball/messages`;

    return {
      getClub: (page: number) =>
        API.get(`${url}/admin?page=${page}`, ApiVersion.V2),
      getUser: (page: number) =>
        API.get(`${userURL}?page=${page}`, ApiVersion.V2),
      getTeam: (page: number) =>
        API.get(`${teamURL}?page=${page}`, ApiVersion.V2),
      getRecipients: (team: boolean) =>
        API.get(
          `${clubURL}/recipients${team ? '?type=team' : ''}`,
          ApiVersion.V1
        ),
      save: (data: RequestBasketballPostMessage) =>
        API.create(url, ApiVersion.V2, data),
      delete: (data: any) => API.delete(url, ApiVersion.V1, data),
    };
  }

  static basketballMyTeam() {
    const teamurl = `basketball/team`;
    const url = `basketball`;

    return {
      createMultiPractices: (data: any) =>
        API.create(`${url}/practices/multi`, ApiVersion.V1, data),
      updateMultiPractices: (data: any) =>
        API.create(`${url}/practices/multi/update`, ApiVersion.V1, data),
      createSmartPractices: (data: any) =>
        API.create(`${url}/practices/smart`, ApiVersion.V1, data),
      getAllCalendarEvents: () => API.get(`${teamurl}/calendar`, ApiVersion.V1),
      getAllFriendlyGames: () =>
        API.get(`${teamurl}/games/friendly`, ApiVersion.V1),
      getAllGames: () => API.get(`${teamurl}/games`, ApiVersion.V1),
      getAllJudgeGames: () => API.get(`${teamurl}/games/judge`, ApiVersion.V1),
      getAllJudgeGameAllocations: (withJudgeAsst: boolean) =>
        API.get(
          teamurl +
            '/games/judge/allocation/preview' +
            (withJudgeAsst === true ? '?withJudgeAsst' : ''),
          ApiVersion.V1
        ),
      getAllPlayers: () => API.get(`${teamurl}/players`, ApiVersion.V1),
      getAllPlayersSelect: () =>
        API.get(`${teamurl}/players/select`, ApiVersion.V1),
      getAllPractices: () => API.get(`${teamurl}/practices`, ApiVersion.V1),
      getAllPracticesSelect: () =>
        API.get(`${teamurl}/practices/select`, ApiVersion.V1),
      getPreviousPracticesSelect: () =>
        API.get(`${teamurl}/practices/select/previous`, ApiVersion.V1),
      removePlayer: (data: any) =>
        API.deleteNoID(`${teamurl}/players/${data.user_id}`, ApiVersion.V1),
      savePlayer: (data: any) =>
        API.create(`${teamurl}/players`, ApiVersion.V1, data),
      allocateJudgeGames: (
        data: RequestBasketballPostTeamJudgesUsersAutoAssignment
      ) => API.create(teamurl + '/games/judge/allocate', ApiVersion.V2, data),
    };
  }

  static basketballPlayers() {
    const url = 'basketball/players';

    return {
      update: (data: BasketballPlayerEditRequest) =>
        API.updateNoID(url, ApiVersion.V2, data),
    };
  }

  static basketballPracticeComments(practice_id: number) {
    const url = `basketball/practices/${practice_id}/comments`;
    return {
      getComments: (page: number) =>
        API.get(`${url}?page=${page}`, ApiVersion.V2),
      addComment: (data: any) => API.create(`${url}`, ApiVersion.V2, data),
    };
  }

  static basketballMyTeamTasks() {
    const teamurl = `basketball/team/tasks`;
    const url = `basketball`;

    return {
      ...API.getCRUDOperations(teamurl, teamurl, ApiVersion.V1),
      assign: (task_id: number, data: any) =>
        API.create(`${teamurl}/${task_id}/assignments`, ApiVersion.V1, data),
      getTaskEvents: (task_id: number) =>
        API.get(`${teamurl}/${task_id}/events`, ApiVersion.V1),
    };
  }

  static basketballPracticeTimes() {
    const teamurl = `basketball/teams`;
    const url = `basketball/practiceTimes`;

    return {
      save: (data: any, teamid: number) =>
        API.create(`${teamurl}/${teamid}/practicetimes`, ApiVersion.V2, data),
      delete: (data: any) => API.delete(url, ApiVersion.V1, data),
    };
  }

  static basketballPractices() {
    const url = `basketball/practices`;

    return {
      getSingle: (id: number) => API.getSingle(url, ApiVersion.V2, id),
      savePractice: (data: any) => API.create(`${url}`, ApiVersion.V2, data),
      createPractices: (data: BasketballPracticesSaveRequest) =>
        API.create(`${url}/multi`, ApiVersion.V2, data),
      updatePractices: (data: BasketballPracticesUpdateRequest) =>
        API.create(`${url}/multi/update`, ApiVersion.V2, data),
      linkPractices: (data: BasketballPracticesLinkRequest) =>
        API.create(`${url}/link`, ApiVersion.V2, data),
      syncPractices: () => API.create(`${url}/sync`, ApiVersion.V2, {}),
      deletePractices: (data: { ids: number[] }) =>
        API.create(`${url}/delete`, ApiVersion.V2, data),
      downloadReport: (id: number) =>
        API.download(url + '/' + id + '/report', ApiVersion.V1),
      updateReply: (data: BasketballPracticeReplyRequest) =>
        API.updateNoID(`${url}/reply`, ApiVersion.V2, data),
      updateReplyAsCoach: (practice_id: number, data: any) =>
        API.updateNoID(`${url}/${practice_id}/coachreply`, ApiVersion.V1, data),
      updateNominees: (
        practice_id: number,
        data: RequestBasketballUpdatePracticeNominees
      ) =>
        API.updateNoID(`${url}/${practice_id}/nominees`, ApiVersion.V2, data),
      updateHelpouts: (data: RequestBasketballPostPracticeHelpouts) =>
        API.create(`helpouts/basketball/practice`, ApiVersion.V1, data),
    };
  }

  static basketballReferee() {
    const usersurl = `club/basketball/referees`;
    const url = `user/basketball/referee`;

    return {
      getAllRefereesSelect: () => API.get(usersurl + '/select', ApiVersion.V1),
      getAllRefereesSelectWithGames: () =>
        API.get(usersurl + '/selectWithGames', ApiVersion.V1),
      getRefereeGames: () => API.get(url + '/games', ApiVersion.V1),
    };
  }

  static basketballRefereeSwitches() {
    const cluburl = `club/basketball/refereeswitches`;
    const url = `basketball/refereeswitches`;

    return {
      getAll: () => API.get(cluburl, ApiVersion.V1),
      create: (data: any) => API.create(url, ApiVersion.V1, data),
      switch: (data: any) => API.update(url, ApiVersion.V1, data),
      delete: (data: any) => API.delete(url, ApiVersion.V1, data),
    };
  }

  static basketballResultReporting() {
    const url = `club/basketball/games/resultreports`;

    return {
      getGames: () => API.get(url, ApiVersion.V1),
      sendReport: (data: any) => API.create(url + '/send', ApiVersion.V1, data),
    };
  }

  static basketballSwitchRequests() {
    const userUrl = `user/basketball/switchrequests`;

    return {
      userSwitchRequests: (id: number | null = null) =>
        API.get(`${userUrl}${id ? '?user_id=' + id : ''}`, ApiVersion.V2),
      possibleJudgeGames: (id: number | null = null) =>
        API.get(
          `${userUrl}/judges/games${id ? '?user_id=' + id : ''}`,
          ApiVersion.V2
        ),
      possibleCoachGames: (id: number | null = null) =>
        API.get(
          `${userUrl}/coach/games${id ? '?user_id=' + id : ''}`,
          ApiVersion.V2
        ),
      possibleCoachPractices: (id: number | null = null) =>
        API.get(
          `${userUrl}/coach/practices${id ? '?user_id=' + id : ''}`,
          ApiVersion.V2
        ),
      possibleRefereeGames: (id: number | null = null) =>
        API.get(
          `${userUrl}/referee/games${id ? '?user_id=' + id : ''}`,
          ApiVersion.V2
        ),
      create: (data: BasketballSwitchRequestCreateRequest) =>
        API.create(`${userUrl}`, ApiVersion.V2, data),
      takeOver: (data: BasketballSwitchRequestTakeOverRequest) =>
        API.update(`${userUrl}`, ApiVersion.V2, data),
      takeOverJudges: (data: BasketballSwitchRequestTakeOverJudgeGameRequest) =>
        API.updateNoID(`${userUrl}/${data.id}/judges`, ApiVersion.V2, data),
      delete: (id: number) => API.delete(userUrl, ApiVersion.V2, { id: id }),
    };
  }

  static basketballTeams() {
    const cluburl = `club/basketball/teams`;
    const url = `basketball/teams`;

    return {
      ...API.getCRUDOperations(cluburl, url, ApiVersion.V1),
      getAllWithPreferredHomeGameData: () =>
        API.get(cluburl + '/preferredHomeGameData', ApiVersion.V1),
      getAllSelect: () => API.get(cluburl + '/select', ApiVersion.V1),
      getPracticeTimes: () =>
        API.get(cluburl + '/practicetimes', ApiVersion.V1),
      updateSettings: (id: number, data: any) =>
        API.updateNoID(url + '/' + id + '/settings', ApiVersion.V1, data),
      savePreferredHomeGameData: (data: any) =>
        API.create(url + '/preferredHomeGameData', ApiVersion.V1, data),
      deletePreferredHomeGameData: (data: any) =>
        API.delete(url + '/preferredHomeGameData', ApiVersion.V1, data),
      syncPracticesUntil: (data: BasketballTeamsSyncPracticesUntilRequest) =>
        API.create(url + '/sync-practices-until', ApiVersion.V1, data),
      /*
       *  Events
       */
      getEvents: (teamId: number) =>
        API.get(`${url}/${teamId}/events`, ApiVersion.V2),
      /*
       *  Judge games
       */
      getJudgeGames: (teamId: number) =>
        API.get(`${url}/${teamId}/games/judges`, ApiVersion.V2),
      /*
       *  Players team actions
       */
      getPlayers: (teamId: number) =>
        API.get(`${url}/${teamId}/players`, ApiVersion.V2),
      getPlayersSelect: (teamId: number) =>
        API.get(`${url}/${teamId}/players/select`, ApiVersion.V2),
      addPlayers: (data: BasketballTeamAddPlayersRequest) =>
        API.create(`${url}/${data.team_id}/players`, ApiVersion.V2, data),
      removePlayers: (data: BasketballTeamRemovePlayersRequest) =>
        API.create(
          `${url}/${data.team_id}/players/remove`,
          ApiVersion.V2,
          data
        ),
      transferPlayers: (data: BasketballTeamTransferPlayersRequest) =>
        API.create(
          `${url}/${data.team_id_from}/players/transfer`,
          ApiVersion.V2,
          data
        ),
      uploadTeamPhoto: (teamId: number, logo: any) =>
        API.uploadFile(`${url}/${teamId}/photo`, ApiVersion.V2, logo),
      uploadTeamPhotoAsBase64: (teamId: number, data: string) =>
        API.create(`${url}/${teamId}/photo`, ApiVersion.V2, { base64: data }),
      deleteTeamPhoto: (teamId: number) =>
        API.deleteNoID(`${url}/${teamId}/photo`, ApiVersion.V2),
    };
  }

  static basketballTeamTaskAssignments() {
    const userUrl = `user/basketball/tasks`;

    return {
      getUserAssignments: (userId: number | null = null) =>
        API.get(
          `${userUrl}${userId ? '?user_id=' + userId : ''}`,
          ApiVersion.V1
        ),
    };
  }

  static basketballUser() {
    const url = `user/basketball`;

    return {
      getAllEvents: (data: BasketballUserEventsRequest) =>
        API.get(
          `${url}/events?${convertObjectToUrlParams(data)}`,
          ApiVersion.V2
        ),
      getAllEventsCalendar: () =>
        API.get(`${url}/events/calendar`, ApiVersion.V1),
      getUpcomingLeagueData: () =>
        API.get(`${url}/upcomingleaguegames`, ApiVersion.V1),
      updateActiveCoachTeam: (data: any) =>
        API.updateNoID(`${url}/activecoachteam`, ApiVersion.V1, data),
      getIcalToken: () => API.get(`${url}/ical/token`, ApiVersion.V1),
      generateIcalToken: () =>
        API.create(`${url}/ical/token`, ApiVersion.V1, []),
    };
  }

  /*
        OTHER ENDPOINTS
     */

  static files() {
    const url = `files`;

    return {
      downloadFile: (fileName: string) =>
        API.download(`${url}/${fileName}`, ApiVersion.V1),
    };
  }

  static jiraIssues() {
    const url = `jira/issues`;

    return {
      getOpenIssues: () => API.get(`${url}/open`, ApiVersion.V1),
    };
  }

  static knowledgeBase() {
    const url = `knowledge-base`;

    return {
      getSearchSuggestions: (query: string) =>
        API.get(`${url}/search/suggestions?q=${query}`, ApiVersion.V1),
      downloadFile: (id: number) =>
        API.download(`${url}/download/${id}`, ApiVersion.V1),
    };
  }

  /*
        BASE FUNCTIONS
     */

  static getCRUDOperations(
    cluburl: string,
    url: string,
    apiVersion: ApiVersion
  ) {
    return {
      getAll: () => API.get(cluburl, apiVersion),
      get: (id: number) => API.getSingle(url, apiVersion, id),
      create: (data: any) => API.create(url, apiVersion, data),
      delete: (data: any) => API.delete(url, apiVersion, data),
      update: (data: any) => API.update(url, apiVersion, data),
    };
  }

  static async download(url: string, version: ApiVersion) {
    const requestUrl = `${API.baseurl}${version}${url}`;
    return await axios.get(requestUrl, {
      responseType: 'arraybuffer',
    });
  }

  static async get(url: string, version: ApiVersion) {
    const requestUrl = `${API.baseurl}${version}${url}`;
    try {
      const res = await axios.get(requestUrl);
      return res;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  }

  static async getSingle(url: string, version: ApiVersion, id: number) {
    const requestUrl = `${API.baseurl}${version}${url}/${id}`;
    try {
      const res = await axios.get(requestUrl);
      return res;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  }

  static async create(url: string, version: ApiVersion, data: any) {
    const requestUrl = `${API.baseurl}${version}${url}`;
    try {
      const res = await axios.post(requestUrl, data);
      return res;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  }

  static async delete(url: string, version: ApiVersion, data: any) {
    const requestUrl = `${API.baseurl}${version}${url}/${data.id}`;
    try {
      const res = await axios.delete(requestUrl);
      return res;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  }

  static async deleteNoID(url: string, version: ApiVersion) {
    const requestUrl = `${API.baseurl}${version}${url}`;
    try {
      const res = await axios.delete(requestUrl);
      return res;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  }

  static async update(url: string, version: ApiVersion, data: any) {
    const requestUrl = `${API.baseurl}${version}${url}/${data.id}`;
    try {
      const res = await axios.put(requestUrl, data);
      return res;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  }

  static async updateNoID(url: string, version: ApiVersion, data: any) {
    const requestUrl = `${API.baseurl}${version}${url}`;
    try {
      const res = await axios.put(requestUrl, data);
      return res;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  }

  static async uploadFile(
    url: string,
    version: ApiVersion,
    file: any,
    additionalData?: any
  ) {
    const formData = new FormData();
    formData.append('file', file);

    if (additionalData) {
      Object.keys(additionalData).forEach((key: string) => {
        formData.append(key, additionalData[key]);
      });
    }

    const config = {
      headers: {
        'content-type': 'multipart/form-data',
      },
    };

    const requestUrl = `${API.baseurl}${version}${url}`;
    try {
      const res = await axios.post(requestUrl, formData, config);
      return res;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  }
}

function convertObjectToUrlParams(obj: any) {
  let str = '';
  for (const key in obj) {
    if (str !== '') {
      str += '&';
    }
    if (obj[key] !== null && obj[key] !== undefined) {
      str += key + '=' + encodeURIComponent(obj[key]);
    }
  }
  return str;
}
